import React, { useRef, useMemo, useCallback, useState } from "react";

// Material ui imports
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import { Theme, Popover, Typography, alpha, Box } from "@mui/material";
import { TemplateNodeContextMenu } from "./TemplateNodeContextMenu";

import FileIcon from "@mui/icons-material/InsertDriveFile";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import classNames from "classnames";

// Redux imports
import { useSelector, useDispatch } from "react-redux";
import { setContextAnchor } from "src/redux/actions/FlightBuilder.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { setActiveTemplateNode, setActiveFileTemplate } from "src/redux/actions/FlightBuilder.Actions";
import { IFlightTemplate } from "@vmosolutions/oc-interfaces";

import { BuildFolderFormDialog, IDialogMessage } from "src/pages/FlightBuilder/Forms/BuildFolderFormDialog";

interface IFileTreeProps {
  expanded: string[];
  handleToggleExpandFolder: (event: React.ChangeEvent<object>, nodeIds: string[]) => void;
}

const getTemplatesByFolder = (templates: IFlightTemplate[], activeNodeId: string): IFlightTemplate[] => {
  return templates.filter((temp: IFlightTemplate) => temp.path.includes(activeNodeId));
};

export const FileTree = (props: IFileTreeProps): JSX.Element => {
  const { expanded, handleToggleExpandFolder } = props;
  const treeBuilderRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const templates = useSelector((state: IRootState) => state.flightBuilderTemplates.templates);
  const activeFolderNode = useSelector((state: IRootState) => state.flightBuilderTemplates.activeNode);
  const activeFileNode = useSelector((state: IRootState) => state.flightBuilderTemplates.activeFileTemplate);
  const anchorEl = useSelector((state: IRootState) => state.flightBuilderTemplates.anchorEl);
  const [buildFolderDialogOpen, setBuildFolderDialogOpen] = useState<boolean>(false);

  // If user has a folder selected, retrieve any templates it may contain to build.
  const tempsToBuild: IFlightTemplate[] = useMemo(() => {
    return templates?.length > 0 && activeFolderNode?._id && !activeFolderNode?.isLeafNode
      ? getTemplatesByFolder(templates, activeFolderNode?._id)
      : [];
  }, [templates, activeFolderNode]);

  const handleContext = useCallback(
    (node: IFlightTemplate) => (event: React.MouseEvent<HTMLLIElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const anchorEl = event.currentTarget ?? treeBuilderRef.current;
      if (node?.isLeafNode) {
        dispatch(setActiveFileTemplate(node));
      } else {
        dispatch(setActiveTemplateNode(node));
      }
      dispatch(setContextAnchor(anchorEl as HTMLDivElement));
    },
    [dispatch]
  );

  const closeContextMenu = (): void => {
    dispatch(setContextAnchor(null));
  };

  const handleAnotherRightClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event.preventDefault();
    closeContextMenu();
  };

  const getTreeFromPath = useCallback(
    (path: string[], templates: IFlightTemplate[], depth: number): JSX.Element => {
      const pathStr = path.join(",");
      const childrenNodes = templates.filter(
        (template: IFlightTemplate) => template.path.length === depth && template.path.join(",").startsWith(pathStr)
      );
      const activeFolderNodeId = activeFolderNode ? activeFolderNode._id : null;
      const activeFileNodeId = activeFileNode ? activeFileNode._id : null;

      return (
        <>
          {childrenNodes.map((node: IFlightTemplate) => {
            if (node.isLeafNode) {
              const isUTCBuildTemplate = !node?.asLocal;
              return (
                <TreeItem
                  classes={{
                    root: classNames(node._id === activeFileNodeId && "active", "file"),
                    label: "item_label file",
                  }}
                  key={node._id}
                  nodeId={node._id}
                  onContextMenu={handleContext(node)}
                  onClick={() => dispatch(setActiveFileTemplate(node))}
                  label={
                    <>
                      <FileIcon color="inherit" className="item_icon" />
                      {node.fileName}
                      {isUTCBuildTemplate && (
                        <Typography color="common.white" sx={{ marginLeft: "10px", fontSize: "10px" }}>
                          UTC
                        </Typography>
                      )}
                    </>
                  }
                />
              );
            }
            return (
              <TreeItem
                classes={{
                  root: classNames(node._id === activeFolderNodeId && "active_folder", "folder"),
                  label: "item_label folder",
                }}
                key={node._id}
                nodeId={node._id}
                onContextMenu={handleContext(node)}
                onClick={() => dispatch(setActiveTemplateNode(node))}
                label={node.fileName}
              >
                {getTreeFromPath([...path, node._id], templates, depth + 1)}
              </TreeItem>
            );
          })}
        </>
      );
    },
    [dispatch, activeFileNode, activeFolderNode, handleContext]
  );

  const templateTree = useMemo(() => {
    if (!!templates && Array.isArray(templates)) {
      const rootNodes = templates.filter((value: IFlightTemplate) => value.path.length === 0);
      if (rootNodes.length === 0) {
        return null;
      }
    }
    return getTreeFromPath([], templates, 0);
  }, [templates, getTreeFromPath]);

  /**
   * Build Folder Dialog info
   */
  const folderBuildInfo: IDialogMessage = useMemo(
    (): IDialogMessage => ({
      type: null,
      title: `Build templates in the "${activeFolderNode?.fileName}" folder`,
      mainMessage:
        "Note: Clicking outside this window will close it but initiated builds will continue to process in the background.",
      yesButton: null,
      noButton: "Close",
    }),
    [activeFolderNode]
  );

  const fileTreeViewStyles = (theme: Theme): any => ({
    root: {
      width: "100%",
      "& .context_listener": {
        "& ul.tree_view": {
          width: "100%",
          margin: 0,
          listStyle: "none",
          "& li.file": {
            "& .MuiTreeItem-iconContainer": {
              display: "none",
            },
            "& .item_label": {
              color: theme.custom.treeview.itemColor,
            },
          },
          "& li.folder": {
            "& .item_label.folder": {
              color: theme.custom.treeview.itemActiveColor,
            },
          },

          "& li.folder, & li.file": {
            "& .MuiTreeItem-iconContainer": {
              width: "18px",
              margin: "0px 4px 3px 6px",
              "& svg": {
                width: "16px",
                color: theme.custom.treeview.itemActiveColor,
              },
              "& .item_label": {
                userSelect: "none",
              },
            },
            "&.active_folder > .MuiTreeItem-content": {
              backgroundColor: alpha(theme.custom.treeview.itemActiveBackground, 0.1),
              "& .MuiTreeItem-iconContainer": {
                "& svg": {
                  color: theme.custom.treeview.itemActiveColor,
                },
              },
              "& .item_label": {
                color: theme.custom.treeview.itemActiveColor,
              },
            },
            "& .item_label": {
              display: "flex",
              alignItems: "center",
              overflowWrap: "break-word",
              whiteSpace: "normal",
              userSelect: "none",
              ...theme.typography.body2,
              color: theme.custom.treeview.itemColor,
              "& .item_icon": {
                width: "16px",
                margin: "0px 10px 0px 2px",
                color: theme.custom.treeview.itemColor,
              },
            },
            "&.active > .MuiTreeItem-content": {
              backgroundColor: theme.custom.treeview.itemActiveBackground,
              "& .item_label": {
                color: theme.custom.treeview.itemActiveColor,
                "& .item_icon": {
                  color: theme.custom.treeview.itemActiveColor,
                },
              },
            },
          },
          "& li.folder:focus > .MuiTreeItem-content": {
            backgroundColor: "transparent",
          },

          "& .MuiTreeItem-group": {
            marginLeft: 0,
            "& li.MuiTreeItem-root .MuiTreeItem-content": { paddingLeft: "10px" },
            "& .MuiTreeItem-group": {
              "& li.MuiTreeItem-root .MuiTreeItem-content": { paddingLeft: "20px" },
              "& .MuiTreeItem-group": {
                "& li.MuiTreeItem-root .MuiTreeItem-content": { paddingLeft: "30px" },
                "& .MuiTreeItem-group": {
                  "& li.MuiTreeItem-root .MuiTreeItem-content": {
                    paddingLeft: "40px",
                  },
                },
              },
            },
          },
        },
      },
    },

    templateContextMenu: {
      boxShadow: theme.shadows[1],
      borderRadius: "3px",
      backgroundColor: alpha(theme.palette.common.black, 0.85),
      fontSize: 10,
      lineHeight: "10px",
      "& .template_popover_menu": {
        padding: 0,
        "& li": {
          minHeight: "24px",
          color: theme.custom.timeline.groupColor,
          padding: "5px 20px",
          fontSize: 14,
          "&:hover": { color: theme.palette.text.primary },
        },
      },
    },
  });

  return (
    <Box sx={(theme) => fileTreeViewStyles(theme).root}>
      <div className="context_listener">
        <TreeView
          className="tree_view"
          defaultCollapseIcon={<FolderOpenIcon className="folder_open_icon" />}
          defaultExpandIcon={<FolderIcon className="folder_icon" />}
          defaultExpanded={expanded}
          expanded={expanded}
          onNodeToggle={handleToggleExpandFolder}
        >
          <TreeItem
            classes={{
              root: classNames(activeFolderNode === null && "active_folder", "folder", "root"),
              label: "item_label folder root",
            }}
            key="root"
            nodeId={"rootNode"}
            onContextMenu={handleContext(null)}
            onClick={() => dispatch(setActiveTemplateNode(null))}
            label="Root"
          >
            {templateTree}
          </TreeItem>
        </TreeView>

        <div ref={treeBuilderRef} className="contextMenuAnchor" />
        <div onContextMenu={handleAnotherRightClick} onClick={closeContextMenu}>
          <Popover
            sx={(theme) => ({ paper: fileTreeViewStyles(theme).templateContextMenu })}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            disableRestoreFocus
          >
            <TemplateNodeContextMenu
              setBuildFolderDialogOpen={setBuildFolderDialogOpen}
              disableBuildFolder={tempsToBuild?.length === 0}
            />
          </Popover>
        </div>
      </div>

      <BuildFolderFormDialog
        open={buildFolderDialogOpen}
        handleClose={setBuildFolderDialogOpen}
        message={folderBuildInfo}
        buildStatusPayload={tempsToBuild}
      />
    </Box>
  );
};
